import React, { useEffect, useMemo, useState } from 'react';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { GridItemSize } from '../../grid/GridItem/GridItem';

/**
 * use a pipeline of filters to filter the items.
 * The filters can then be rendered via the handle. (e.g. by passing it to the FilterBar)
 *
 * usage example
 *
 * const MyComponent = (props: { items: Array<any> }) => {
 *   const { items } = props;
 *
 *   const filters = useMemo(() => {
 *     return [
 *       {
 *         defaultValue: null,
 *         filter: (item, value) => item.x === value,
 *         render: ({ value, setValue }) => {
 *           return (
 *            <SearchableSelect
 *              ....
 *              value={value}
 *              onValueChanged={setValue}
 *            >
 *            </SearchableSelectProps>
 *           );
 *         }
 *       }
 *     ];
 *   }, []);
 *
 *   const { handle, filteredItems } = useFilterPipeline({ items, filters });
 *
 *   return (
 *     <div>
 *      <FilterBar handle={handle}></FilterBar>
 *      {filteredItems.map(item => generateJsxForItem(item))}
 *     </div>
 *   )
 * }
 *
 */
export function useFilterPipeline<T>({
  items,
  filters
}: UseFilterPipelineOptions<T>): UseFilterPipelineReturnValue<T> {
  const { getFilterContext, getFilterValueSetter } = useFilterContexts(filters);

  const filteredEntities = useMemo(() => {
    return filters.reduce((lastFilterResult, filter) => {
      const context = getFilterContext(filter);
      if (!context) {
        return lastFilterResult;
      }

      return lastFilterResult.filter((item) =>
        filter.filter(item, context.value)
      );
    }, items);
  }, [items, filters, getFilterContext]);

  const handle = useMemo<FilterPipelineHandle>(
    () => ({
      renderFilters: () => {
        const renderedFilters: Array<RenderedFilterPipelineFilter> = [];

        for (const filter of filters) {
          const context = getFilterContext(filter);
          if (!context) {
            continue;
          }

          renderedFilters.push({
            id: context.id,
            element: (
              <filter.Component
                value={context.value}
                setValue={getFilterValueSetter(filter)}
              ></filter.Component>
            ),
            size: filter.size ?? 2
          });
        }

        return renderedFilters;
      }
    }),
    [filters, getFilterContext, getFilterValueSetter]
  );

  return {
    filteredItems: filteredEntities,
    handle
  };
}

function useFilterContexts<T>(filters: Array<FilterPipelineFilter<T, any>>): {
  /**
   * Due to react effects being async, a null filter context can be returned here.
   * If null is returned, then you can just ignore that filter for now, another rerender will occur where this is defined.
   * This mostly happens when dynamically adding filters
   */
  getFilterContext: <TValue>(
    filter: FilterPipelineFilter<T, TValue>
  ) => FilterContext<TValue> | null;
  getFilterValueSetter: <TValue>(
    filter: FilterPipelineFilter<T, TValue>
  ) => (value: TValue) => void;
} {
  const [filterContexts, setFilterContexts] = useState(() => {
    return new Map(
      filters.map((filter) => [filter, createFilterContext(filter)])
    );
  });

  const filterValueSetters = useFilterValueSetters(filters, setFilterContexts);

  useEffect(() => {
    setFilterContexts((oldFilterContexts) => {
      const newFilterContexts = new Map(oldFilterContexts);
      let modified = false;

      for (const filter of filters) {
        if (!newFilterContexts.has(filter)) {
          newFilterContexts.set(filter, createFilterContext(filter));
          modified = true;
        }
      }

      for (const key of newFilterContexts.keys()) {
        if (!filters.includes(key)) {
          newFilterContexts.delete(key);
          modified = true;
        }
      }

      return modified ? newFilterContexts : oldFilterContexts;
    });
  }, [filters]);

  return useMemo(() => {
    return {
      getFilterContext: (filter) => {
        return filterContexts.get(filter) ?? null;
      },
      getFilterValueSetter: (filter) => {
        const setter = filterValueSetters.get(filter);
        assertNotNullOrUndefined(setter, 'no setter found');
        return setter;
      }
    };
  }, [filterContexts, filterValueSetters]);
}

function useFilterValueSetters<T>(
  filters: Array<FilterPipelineFilter<T, any>>,
  setFilterContexts: React.Dispatch<
    React.SetStateAction<Map<FilterPipelineFilter<T, any>, FilterContext<any>>>
  >
): Map<FilterPipelineFilter<T, any>, (value: any) => void> {
  return useMemo(() => {
    return new Map(
      filters.map((filter) => {
        return [
          filter,
          (value) => {
            setFilterContexts((oldFilterContexts) => {
              const newFilterContexts = new Map(oldFilterContexts);
              const context = getRequiredContext(newFilterContexts, filter);

              newFilterContexts.set(filter, {
                ...context,
                value
              });

              return newFilterContexts;
            });
          }
        ];
      })
    );
  }, [filters, setFilterContexts]);
}

function getRequiredContext<T>(
  contexts: Map<FilterPipelineFilter<T, any>, FilterContext<any>>,
  filter: FilterPipelineFilter<T, any>
): FilterContext<any> {
  const context = contexts.get(filter);
  assertNotNullOrUndefined(context, 'no context found');
  return context;
}

let lastFilterId = 0;
function createFilterContext<TValue>(
  filter: FilterPipelineFilter<unknown, TValue>
): FilterContext<TValue> {
  return {
    id: ++lastFilterId,
    value: filter.defaultValue
  };
}

export type UseFilterPipelineOptions<T> = {
  items: Array<T>;
  filters: Array<FilterPipelineFilter<T, any>>;
};

export type UseFilterPipelineReturnValue<T> = {
  filteredItems: Array<T>;
  handle: FilterPipelineHandle;
};

/**
 * A single filter of the filter pipeline
 * Them same instance of the filter should be used for all renderings so they can be tracked correctly
 * This can be achieved with a useMemo or defining the filter globally
 */
export type FilterPipelineFilter<TItem, TValue> = {
  defaultValue: TValue;
  filter(item: TItem, value: TValue): boolean;
  Component(options: {
    value: TValue;
    setValue: (value: TValue) => void;
  }): JSX.Element;
  /**
   * default: 2
   */
  size?: GridItemSize;
};

export type FilterPipelineHandle = {
  renderFilters: () => Array<RenderedFilterPipelineFilter>;
};

export type RenderedFilterPipelineFilter = {
  id: number;
  size: GridItemSize;
  element: JSX.Element;
};

type FilterContext<TValue> = {
  id: number;
  value: TValue;
};
